// src/components/faqs/FAQList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchFaqs,
  deleteFaq,
  updateFaqStatus,
  clearFaqError,
  clearFaqSuccess,
  setFaqFilters,
} from '../../store/actions/faqActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import FilterBar from '../common/FilterBar';

const FAQList = () => {
  const dispatch = useDispatch();
  const { faqs, loading, error, success, meta, filters } = useSelector(
    (state) => state.faqs
  );
  const { categories } = useSelector((state) => state.categories);

  const [selectedFaq, setSelectedFaq] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'DRAFT', label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
    { value: 'PENDING_REVIEW', label: 'En attente de relecture', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'PUBLISHED', label: 'Publié', color: '#10b981', bg: '#d1fae5' },
    { value: 'SCHEDULED', label: 'Programmé', color: '#3b82f6', bg: '#dbeafe' },
    { value: 'ARCHIVED', label: 'Archivé', color: '#6b7280', bg: '#e5e7eb' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    bulkDeleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    bulkDeleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    bulkDeleteButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    filterBar: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellCheckbox: {
      padding: '0.75rem 1.5rem',
      width: '40px',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellCheckbox: {
      padding: '1rem 1.5rem',
      width: '40px',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    faqInfo: {
      maxWidth: '400px',
    },
    faqQuestion: {
      fontWeight: 600,
      color: '#111827',
      textDecoration: 'none',
      display: 'block',
      marginBottom: '0.25rem',
    },
    faqAnswer: {
      fontSize: '0.75rem',
      color: '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    priorityBadge: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    categoryTag: {
      display: 'inline-flex',
      padding: '0.125rem 0.375rem',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.625rem',
      fontWeight: 500,
    },
    stats: {
      display: 'flex',
      gap: '1rem',
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    helpful: {
      color: '#10b981',
    },
    notHelpful: {
      color: '#dc2626',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    feedbackButton: {
      color: '#0891b2',
    },
    feedbackButtonHover: {
      color: '#0e7490',
    },
    statusButton: {
      color: '#f59e0b',
    },
    statusButtonHover: {
      color: '#d97706',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    statusModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    statusModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '90%',
    },
    statusModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    statusOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    statusOptionSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionRadio: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
  };

  const getStatusStyle = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return {
      backgroundColor: option?.bg || '#f3f4f6',
      color: option?.color || '#374151',
    };
  };

  const fetchFaqsData = useCallback(() => {
    dispatch(fetchFaqs(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchFaqsData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchFaqsData();
      }
    }
  }, [fetchFaqsData, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearFaqError());
      dispatch(clearFaqSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    dispatch(setFaqFilters({
      ...filters,
      search: searchTerm,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch(setFaqFilters({
      ...filters,
      ...newFilters,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handlePageChange = useCallback((page) => {
    dispatch(setFaqFilters({
      ...filters,
      page,
    }));
  }, [dispatch, filters]);

  const handleDeleteClick = useCallback((faq) => {
    setSelectedFaq(faq);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteFaq(selectedFaq.id));
      setShowDeleteConfirm(false);
      setSelectedFaq(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedFaq]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedFaq(null);
  }, []);

  const handleStatusClick = useCallback((faq) => {
    setSelectedFaq(faq);
    setStatusFilter(faq.status);
    setShowStatusModal(true);
  }, []);

  const handleStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateFaqStatus(selectedFaq.id, statusFilter));
      setShowStatusModal(false);
      setSelectedFaq(null);
      setStatusFilter('');
    } catch (error) {
      console.error('Status update failed:', error);
    }
  }, [dispatch, selectedFaq, statusFilter]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(faqs.map(faq => faq.id));
    } else {
      setSelectedIds([]);
    }
  }, [faqs]);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    try {
      // await dispatch(bulkDeleteFaqs(selectedIds));
      setShowBulkDeleteConfirm(false);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  }, [dispatch, selectedIds]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading && !faqs.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des FAQs</h1>
        <div style={styles.headerActions}>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              style={{
                ...styles.bulkDeleteButton,
                ...(selectedIds.length === 0 ? styles.bulkDeleteButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButton.backgroundColor;
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer ({selectedIds.length})
            </button>
          )}
          <Link
            to="/admin/faqs/form"
            style={styles.createButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouvelle FAQ
          </Link>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearFaqError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearFaqSuccess())}
        />
      )}

      <div style={styles.filterBar}>
        <FilterBar
          filters={[
            {
              type: 'select',
              name: 'status',
              label: 'Statut',
              options: statusOptions,
            },
            {
              type: 'select',
              name: 'categoryId',
              label: 'Catégorie',
              options: [
                { value: '', label: 'Toutes les catégories' },
                ...categories.map(c => ({ value: c.id, label: c.name })),
              ],
            },
          ]}
          onFilterChange={handleFilterChange}
          initialValues={filters}
        />
      </div>

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher des FAQs par question ou réponse..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCellCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === faqs.length && faqs.length > 0}
                  onChange={handleSelectAll}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeaderCell}>Question</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Catégorie</th>
              <th style={styles.tableHeaderCell}>Priorité</th>
              <th style={styles.tableHeaderCell}>Statistiques</th>
              <th style={styles.tableHeaderCell}>Date</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {faqs.map((faq) => (
              <tr 
                key={faq.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCellCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(faq.id)}
                    onChange={() => handleSelectOne(faq.id)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.faqInfo}>
                    <Link to={`/admin/faqs/${faq.id}/details`} style={styles.faqQuestion}>
                      {faq.question}
                    </Link>
                    <div style={styles.faqAnswer}>
                      {faq.answer?.replace(/<[^>]*>/g, '').substring(0, 100)}...
                    </div>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span
                    style={{
                      ...styles.badge,
                      ...getStatusStyle(faq.status),
                    }}
                  >
                    {statusOptions.find(s => s.value === faq.status)?.label || faq.status}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  {faq.category ? (
                    <span style={styles.categoryTag}>
                      {faq.category.name}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={styles.tableCell}>
                  <span style={{ ...styles.badge, ...styles.priorityBadge }}>
                    P{faq.priority || 0}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.stats}>
                    <span style={styles.statItem} title="Vues">
                      👁️ {faq.views || 0}
                    </span>
                    <span style={{ ...styles.statItem, ...styles.helpful }} title="Utile">
                      👍 {faq.helpfulCount || 0}
                    </span>
                    <span style={{ ...styles.statItem, ...styles.notHelpful }} title="Pas utile">
                      👎 {faq.notHelpfulCount || 0}
                    </span>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div>{formatDate(faq.publishedAt || faq.createdAt)}</div>
                  {faq.lastReviewedAt && (
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      Révisé: {formatDate(faq.lastReviewedAt)}
                    </div>
                  )}
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/faqs/${faq.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="Voir les détails"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/faqs/${faq.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Modifier"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/faqs/${faq.id}/feedback`}
                      style={{
                        ...styles.actionButton,
                        ...styles.feedbackButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.feedbackButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.feedbackButton.color;
                      }}
                      title="Voir les retours"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleStatusClick(faq)}
                      style={{
                        ...styles.actionButton,
                        ...styles.statusButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.statusButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.statusButton.color;
                      }}
                      title="Changer le statut"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(faq)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Supprimer"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {faqs.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucune FAQ trouvée correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              Changer le statut de la FAQ
            </h3>
            <div style={styles.statusOptions}>
              {statusOptions.filter(s => s.value).map((option) => (
                <div
                  key={option.value}
                  style={{
                    ...styles.statusOption,
                    ...(statusFilter === option.value ? styles.statusOptionSelected : {}),
                  }}
                  onClick={() => setStatusFilter(option.value)}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={statusFilter === option.value}
                    onChange={() => setStatusFilter(option.value)}
                    style={styles.statusOptionRadio}
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: option.bg,
                      color: option.color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={statusFilter === selectedFaq?.status}
                style={{
                  ...styles.saveButton,
                  opacity: statusFilter === selectedFaq?.status ? 0.5 : 1,
                  cursor: statusFilter === selectedFaq?.status ? 'not-allowed' : 'pointer',
                }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer la FAQ"
        message={`Êtes-vous sûr de vouloir supprimer cette FAQ ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Supprimer plusieurs FAQs"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} FAQ(s) ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default FAQList;